import React, { useMemo } from "react";
import {
  FREQUENCY_OPTIONS,
  MIXED_VALUE,
  derivePeriods,
  frequencyLabel,
} from "./helpers";

/**
 * One KPI's frequency row: selection checkbox, title, the cadence select and a
 * preview of the periods that cadence produces for the financial year.
 *
 * The select always names a real cadence — the one this KPI reports at, whether
 * it was set here or comes from the financial-year default — so there is no
 * "same as the default" choice sitting next to the cadence it resolves to. That
 * a cadence was set at the selected sites is left unmarked: the select already
 * shows what the KPI reports at, and a badge on all but a handful of 92 rows
 * reads as decoration rather than as information. Only the cases a person has to
 * act on are chipped — unsaved edits, a disagreement across sites, and a KPI
 * falling back to the year's default.
 *
 * When several locations are being edited and they disagree on this KPI, the
 * select reads "Mixed" and previews no periods: there is no one cadence to
 * show, and picking any option applies it to every selected location. Such a
 * row can be opened to see what each location reports at today — the only place
 * that answer exists once the cadences have been folded into one list.
 *
 * Memoised and fed only primitives plus stable callbacks, so re-rendering the
 * list after one row changes does not re-render the rest. `breakdown` is null
 * for every collapsed row, so opening one leaves the others untouched.
 * @param {object} props the row's data and handlers.
 * @returns {JSX.Element} the row.
 */
function KpiFrequencyRow({
  kpiId,
  title,
  value,
  isScoped,
  isOverride,
  isDirty,
  isSelected,
  isMixed,
  isExpanded,
  breakdown,
  startMonth,
  fyValue,
  disabled,
  onToggleSelect,
  onToggleBreakdown,
  onChange,
}) {
  const isMixedValue = value === MIXED_VALUE;
  const periods = useMemo(
    () => derivePeriods(value, startMonth, fyValue),
    [value, startMonth, fyValue],
  );

  const selectId = `kfq-select-${kpiId}`;
  const breakdownId = `kfq-breakdown-${kpiId}`;
  const setHereCount = (breakdown || []).filter((site) => !site.isInherited).length;

  return (
    <div className={`kfq-row${isDirty ? " kfq-row--dirty" : ""}`}>
      <div className="kfq-row-line">
        <input
          type="checkbox"
          className="kfq-checkbox"
          checked={isSelected}
          disabled={disabled}
          onChange={() => onToggleSelect(kpiId)}
          aria-label={`Select ${title}`}
        />

        <div className="kfq-row-main">
          <div className="kfq-row-title">
            {title}
            {isDirty && <span className="kfq-chip kfq-chip--dirty">Unsaved</span>}
            {!isDirty && isMixed && <span className="kfq-chip kfq-chip--mixed">Mixed</span>}
            {!isDirty && !isMixed && !isOverride && isScoped && (
              <span className="kfq-chip kfq-chip--inherited">Inherited</span>
            )}
          </div>
          <div className="kfq-row-meta">
            {periods.length > 0 && (
              <span className="kfq-periods">
                {periods.map((period) => (
                  <span key={period.key} className="kfq-period">{period.label}</span>
                ))}
              </span>
            )}
            {periods.length === 0 && (
              <span className="kfq-periods-note">
                {isMixedValue
                  ? "Cadence differs across the selected locations"
                  : "Reporter picks a from/to date range at data entry"}
              </span>
            )}
            {isMixed && (
              <button
                type="button"
                className="kfq-linkbtn"
                onClick={() => onToggleBreakdown(kpiId)}
                aria-expanded={isExpanded}
                aria-controls={breakdownId}
              >
                <i
                  className={`fas fa-chevron-${isExpanded ? "up" : "down"}`}
                  aria-hidden="true"
                />
                {isExpanded ? "Hide locations" : "Show all locations"}
              </button>
            )}
          </div>
        </div>

        <div className="kfq-row-control">
          <label className="kfq-visually-hidden" htmlFor={selectId}>
            Reporting frequency for {title}
          </label>
          <select
            id={selectId}
            className="kfq-select"
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(kpiId, e.target.value)}
          >
            {isMixedValue && <option value={MIXED_VALUE}>Mixed</option>}
            {FREQUENCY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
      </div>

      {isMixed && isExpanded && breakdown && (
        <div className="kfq-breakdown" id={breakdownId}>
          <div className="kfq-breakdown-head">
            <span className="kfq-breakdown-title">Cadence by location</span>
            <span className="kfq-breakdown-counts">
              {breakdown.length} location(s) · {setHereCount} set here ·{" "}
              {breakdown.length - setHereCount} inherited
            </span>
            {isDirty && (
              <span className="kfq-breakdown-pending">
                Saving sets all {breakdown.length} to {frequencyLabel(value)}
              </span>
            )}
          </div>
          <ul className="kfq-breakdown-list">
            {breakdown.map((site) => (
              <li
                key={site.key}
                className={`kfq-breakdown-item${site.isInherited ? " kfq-breakdown-item--inherited" : ""}`}
              >
                <span className="kfq-breakdown-loc" title={site.label}>{site.label}</span>
                <span className="kfq-breakdown-freq">
                  {frequencyLabel(site.frequency)}
                  {site.isInherited && <span className="kfq-breakdown-note">inherited</span>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default React.memo(KpiFrequencyRow);
